import React, { createContext, useContext, useState, useEffect } from 'react';
import translations from './translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Namespaces whose text can be overridden via the admin content editor.
const OVERRIDABLE_NAMESPACES = ['about', 'fcf'];
const API_URL = process.env.REACT_APP_API_URL || '';

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage for saved language preference
    const saved = localStorage.getItem('fm_language');
    return saved || 'en';
  });

  // Editable page-content overrides fetched from the backend.
  // Shape: { about: { en: {...}, sl: {...} }, fcf: {...} }
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    localStorage.setItem('fm_language', language);
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    let active = true;
    fetch(`${API_URL}/api/content`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data && data.content) setOverrides(data.content);
      })
      .catch(() => {
        // Non-fatal: pages fall back to translations.js defaults.
      });
    return () => {
      active = false;
    };
  }, []);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'sl' : 'en');
  };

  const t = (key) => {
    const keys = key.split('.');

    // Prefer an admin override for editable namespaces (e.g. "about.companyName").
    if (keys.length === 2 && OVERRIDABLE_NAMESPACES.includes(keys[0])) {
      const override = overrides?.[keys[0]]?.[language]?.[keys[1]];
      if (override) return override;
    }

    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key; // Return the key if translation not found
      }
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;

